/**
 * 행 그리드 등록형 위젯(RegisteredRowWidget)을 폼 위젯 계약(ComposerRegisteredFormWidget)으로
 * 감싸는 어댑터 + 브리지 레지스트리(2026-07-20 P4b).
 *
 * 배경: 위험도('risk-estimation')·개선계획('improvement-plan')은 행 그리드용 레지스트리
 * `COMPOSED_ROW_WIDGETS`(계약 {value,onChange,editable,context})에만 등록돼 있어, 반복(repeat)
 * 행 안에 뼈대로 배치되면 트리 렌더(renderRegisteredWidget — 폼 위젯 레지스트리 조회)가 찾지 못해
 * placeholder("작성 화면에서 동작하는 위젯입니다")로 폴백됐다. 이 어댑터로 행 위젯을 폼 위젯 계약에
 * 브리지해, web 실폼·admin 캔버스(라이브)가 같은 레지스트리를 spread 주입하면 실위젯이 렌더된다
 * (WYSIWYG 단일 정본).
 */
import {
  isLabelHidden,
  labelPresentationFor,
  WidgetLabelContent,
  type ComposerRegisteredFormWidget,
  type ComposerRegisteredFormWidgetProps,
} from './composerWidgetTreeRender';
import { COMPOSED_ROW_WIDGETS } from './registeredRowWidgets';
import type { RegisteredRowWidget } from './RowsCollectionEditor';

/**
 * 행 위젯 → 폼 위젯 어댑터.
 * - value = values[widget.bindingKey] — 반복 행 스코프면 values=행 객체이므로 bindingKey(행 상대키)로
 *   자연 라우팅. 본문 스코프 배치 시에도 같은 규약(값=본문 키).
 * - onChange(v) → onChange(widget.bindingKey, v) — 폼 위젯 계약의 (key,value) 시그니처로 어댑트.
 * - editable = widget.mode !== 'readonly' (뼈대 field.editable:false → mode 'readonly'로 매핑됨).
 * - context = 행 위젯이 기대하는 { values, resolvedRows } 형상으로 합성. 폼 위젯 context는 불투명
 *   통로라 그대로 얹고, values는 본문 채널(context.values) 우선 · 없으면 현재 스코프 값을 넘긴다.
 *   반복(repeat) 행 스코프에선 렌더 스코프 값=행 객체라 본문 키(기준표 'risk-assessment-config')가
 *   없으므로, RepeatRowsBlock이 context.values에 본문 값을 실어 보내면 그것을 폴백으로 쓴다(P4d).
 *   본문 직접 배치 시엔 context.values가 없어 종전대로 렌더 스코프 값 전달(회귀 0).
 *   (RiskEstimationWidget이 resolvedRows["risk-assessment-config"] ?? values["risk-assessment-config"]로
 *   기준표 조회.)
 * - 라벨: 행 위젯(위험도·개선계획)은 자체 상단 라벨을 그리지 않아, 같은 개념 위젯이 여럿 배치될 때
 *   (개선 전/후 위험도) 구분이 안 된다. 트리 렌더러(renderRegisteredWidget)는 등록형 라벨을 위젯에
 *   위임하므로, 브리지가 일반 input과 같은 규약(labelPresentationFor + WidgetLabelContent —
 *   병기·required *·labelHint 동형)으로 widget.label을 위젯 위에 그린다(2026-07-20, 2026-08-24
 *   조립 단일화). 라벨 미설정 위젯은 라벨 없이 렌더(회귀 0).
 */
export function rowWidgetAsFormWidget(
  RowWidget: RegisteredRowWidget,
): ComposerRegisteredFormWidget {
  const FormWidget = ({ widget, values, onChange, context }: ComposerRegisteredFormWidgetProps) => {
    // scopeValues = 현재 렌더 스코프 값(반복 행 스코프면 **행 객체**) — 행의 이웃 필드를 읽어야
    // 하는 위젯(improvement-image: riskSummary·improvementPlan 등)의 채널. values는 본문 폴백(P4d)이라
    // 행 객체가 아니다. 행 그리드(RowsCollectionEditor)도 같은 키로 row를 싣는다.
    const rowContext = { ...context, values: context?.values ?? values, scopeValues: values };
    // 라벨 프리미티브는 `InputWidget`을 직접 받으므로 변환 없이 그대로 넘긴다(캐스트 없음).
    const node = widget;
    const labelPres = labelPresentationFor(node);
    return (
      <div>
        {widget.label && !isLabelHidden(node) ? (
          <div className={labelPres.className} style={labelPres.style}>
            {/* 내용 조립은 WidgetLabelContent 단일 정본 — 일반 input과 동형(병기·hint 포함). */}
            <WidgetLabelContent node={node} values={values} />
          </div>
        ) : null}
        <RowWidget
          value={values[widget.bindingKey]}
          editable={widget.mode !== 'readonly'}
          onChange={(next) => onChange(widget.bindingKey, next)}
          context={rowContext}
        />
      </div>
    );
  };
  return FormWidget;
}

/**
 * 행 그리드 등록형 위젯을 폼 위젯 계약으로 감싼 레지스트리 — web·admin host가 폼 위젯 레지스트리에
 * spread 주입한다. 반복 행 스코프에 배치된 행 위젯을 트리 렌더가 실위젯으로 그린다(WYSIWYG).
 * placeholder는 진짜 미등록 키만 남는다.
 */
export const COMPOSED_ROW_FORM_WIDGETS: Record<string, ComposerRegisteredFormWidget> =
  Object.fromEntries(
    Object.entries(COMPOSED_ROW_WIDGETS).map(([key, RowWidget]) => [
      key,
      rowWidgetAsFormWidget(RowWidget),
    ]),
  );
